import "cmp-web-templates/scss/cmp.scss";
import {UISelection} from "../../u-i-selection";
import cmp, {site} from "../../../context/context";
import {Disclosure} from "../../../model/vendor";
import traduccion from "cmp-web-translations/modules/traduccion/cmp/secondary_tab";
import {UIEvent} from "../../u-i-event";
import {TEvent, TEventSubtype} from "services-comun-meteored/modules/services/cmp/web/events";
import {IIdioma} from "services-comun/modules/net/idiomas";
import AnalyticManager from "../../../analytic/analytic-manager";
import {EAction, ELabel} from "../../../analytic/event-definition";
import {UISelectionBuilderDefaultRejected} from "../../u-i-selection-builder/u-i-selection-builder-default-rejected";
import {publish} from "../../../util/js-event";
import {UICallback} from "../../u-i-manager";
import {CMPWindow} from "../c-m-p-window";
import {ECMPWindow} from "../interface";

interface ILangCMP {
    back: string;
    almacena_info: string;
    props_caracts: string;
    tiempo_almacena: string;
    props_li: string;
    empresas: string;
    propositos: string;
    propositos_esp: string;
    caracteristicas: string;
    caracteristicas_esp: string;
    aceptar: string;
    rechazar: string;
    aceptar_todo: string;
    rechazar_todo: string;
    guardar_cerrar: string;
    aceptar_todo_y_guardar: string;
    consent_expl: string;
    consent_li: string;
    empresas_no_iab: string;
    politica_privacidad: string;
    cookies_almacena_cookies: string;
    cookies_almacena_otro: string;
    mas_info: string;
    dominio: string;
    categorias_datos: string;
    tiempo_retencion: string;
    tiempo_retencion_msg: string;
    interes_legitimo: string;
    obligatorio: string;
}

export enum EPropVendLayerEvent {
    ACEPTAR_TODO    = 'EV_PropVend_AceptarTodo',
    GUARDAR         = 'EV_PropVend_Guardar',
    CLOSE           = 'EV_PropVend_Close'
}

export interface IAcceptAllEvent {
    callbacks: UICallback[];
}

export interface ISaveEvent {
    allSelected: boolean;
    allRejected: boolean;
    callbacks: UICallback[];
}

export interface ICloseEvent {
    allSelected: boolean;
}

export class CMPManager extends CMPWindow {
    /* STATIC */

    private static _INSTANCE: CMPManager;

    public static instance(): CMPManager {
        return this._INSTANCE ??= new CMPManager();
    }

    /* INSTANCE */
    private readonly SELECTION: UISelection;
    private _LANG: ILangCMP|undefined;
    private _panel: HTMLElement|undefined;

    private constructor() {
        super(ECMPWindow.CONFIGURATION);
        this.SELECTION = new UISelectionBuilderDefaultRejected().build();
    }

    private get panel(): HTMLElement {
        if (!this._panel) {
            throw new Error('CMP panel not found');
        }
        return this._panel;
    }

    /**
     * Controla la acción de abrir la lista de propósitos.
     * @param callbacks Callbacks a ejecutar al cerrar la capa.
     */
    public openPurposes(callbacks: UICallback[] = []): void {
        this.buildMainLayer().then(async (mainContainer: HTMLElement) => {
            this._panel = mainContainer.getElementsByClassName('cmp')[0] as HTMLElement;
            this.openSecondaryLayer(callbacks).then(() => {
                // Lanzamos el evento de apertura
                const event = UIEvent.create(TEvent.APARECE_CMP);
                event.register();
                event.notify();
            });
        });
    }

    /**
     * Controla la acción de abrir la lista de vendors.
     * @param callbacks Callbacks a ejecutar al cerrar la capa.
     */
    public openVendors(callbacks: UICallback[] = []): void {
        this.buildMainLayer().then(async (mainContainer: HTMLElement) => {
            this._panel = mainContainer.getElementsByClassName('cmp')[0] as HTMLElement;
            this.openSecondaryLayer(callbacks).then(() => {
                // Lanzamos el evento de apertura
                const event = UIEvent.create(TEvent.SHOW_SIMPLY_VENDORS);
                event.register();
                event.notify();

                const element: HTMLElement|null = (mainContainer.firstElementChild as HTMLElement).querySelector('#tab-content6');
                if (element) {
                    element.scrollIntoView();
                }
            });
        });
    }

    /**
     * Construye la capa principal del Cmp.
     */
    private async buildMainLayer(): Promise<HTMLElement> {
        const template = await import(/* webpackChunkName: "main" */ '../../template/cmp-v2/main.pug' + '');
        const html: string = template.default({});
        const element: HTMLDivElement = document.createElement('div');
        element.innerHTML = html;

        const mainContainer: HTMLElement = <HTMLElement>element.firstElementChild;

        document.body.insertAdjacentElement('beforeend', mainContainer);

        return mainContainer;
    }

    /**
     * Añade la capa secundaria del Cmp.
     * @param callbacks Callbacks a ejecutar al cerrar la capa.
     */
    private async openSecondaryLayer(callbacks: UICallback[]): Promise<void> {
        const main = this.panel;

        const lang: ILangCMP = await this.loadI18N();
        const template = await import(/* webpackChunkName: "secondary" */ '../../template/cmp-v2/secondary.pug' + '');

        const html: string = template.default({
            selection: this.SELECTION,
            vendors: cmp().vendors(),
            vendorsLI: cmp().vendorsLI(),
            purposes: cmp().purposes(),
            purposesLI: cmp().purposesLI(),
            specialPurposes: cmp().specialPurposes(),
            features: cmp().features(),
            specialFeatures: cmp().specialFeatures(),
            addVendors: cmp().additionalVendors(),
            customVendors: cmp().customVendors(),
            logo_url: site.cmpParams()?.['logo'],
            logo_alt: site.cmpParams()?.['logo_alt'],
            lang: {
                tabs: {
                    pur_car_title: lang.props_caracts,
                    pur_li_title: lang.props_li,
                    partners: lang.empresas,
                },
                partners: lang.empresas,
                purposes: lang.propositos,
                purposes_li: lang.props_li,
                spec_purposes: lang.propositos_esp,
                characteristics: lang.caracteristicas,
                spec_characteristics: lang.caracteristicas_esp,
                accept: lang.aceptar,
                reject: lang.rechazar,
                accept_all: lang.aceptar_todo,
                reject_all: lang.rechazar_todo,
                save_and_close: lang.guardar_cerrar,
                accept_all_and_save: lang.aceptar_todo_y_guardar,
                explicit_consent_title: lang.consent_expl,
                li_consent_title: lang.consent_li,
                no_iab_partner_title: lang.empresas_no_iab,
                privacy_policy: lang.politica_privacidad,
                back: lang.back,
                almacena_info: lang.almacena_info,
                tiempo_almacena: lang.tiempo_almacena,
                cookies_almacena_cookies: lang.cookies_almacena_cookies,
                cookies_almacena_otro: lang.cookies_almacena_otro,
                mas_info: lang.mas_info,
                categorias_datos: lang.categorias_datos,
                tiempo_retencion: lang.tiempo_retencion,
                tiempo_retencion_msg: lang.tiempo_retencion_msg,
                interes_legitimo: lang.interes_legitimo,
                obligatorio: lang.obligatorio
            }
        });
        main.insertAdjacentHTML('afterbegin', html);

        const manageLIs = (list: HTMLElement) => {
            if (list) {
                const lis: HTMLLIElement[] = Array.from(list.querySelectorAll('li.opt'));
                lis.forEach(li => {

                    const title: HTMLElement|null = li.querySelector('.opt-title');
                    if (title) {
                        title.onclick = () => {
                            if (li.classList.contains('open')) {
                                li.classList.remove('open');
                            } else {
                                li.classList.add('open');
                            }
                        };
                    }
                });
            }
        }

        const manageVendors = (list: HTMLElement) => {
            if (list) {
                const lis: HTMLLIElement[] = Array.from(list.querySelectorAll('li.opt'));
                lis.forEach(li => {
                    const masInfoElement: HTMLElement|null = li.querySelector('.vendor-mas-info');
                    if (masInfoElement) {
                        masInfoElement.onclick = async () => {
                            const disclosures: Disclosure[] = await cmp().vendors().find(vendor => vendor.id == parseInt(masInfoElement.dataset['v']??'0'))?.disclosures()??[];
                            const disclosuresTpl: any = await import(/* webpackChunkName: "vendor-disclosures" */ '../../template/cmp-v2/vendor-disclosures.pug' + '');
                            const html: string = disclosuresTpl.default({
                                disclosures: disclosures,
                                lang: {
                                    domain: lang.dominio,
                                    tiempo_almacena: lang.tiempo_almacena,
                                    purposes: lang.propositos,
                                }
                            });
                            masInfoElement.insertAdjacentHTML("afterend", html);
                        };
                    }
                });
            }
        }

        const purposesList: HTMLElement = <HTMLElement>main.querySelector('#tab-content1');
        const specialPurposesList: HTMLElement = <HTMLElement>main.querySelector('#tab-content2');
        const featuresList: HTMLElement = <HTMLElement>main.querySelector('#tab-content3');
        const specialFeaturesList: HTMLElement = <HTMLElement>main.querySelector('#tab-content4');
        const purposesLIList: HTMLElement = <HTMLElement>main.querySelector('#tab-content5');
        const vendorsList: HTMLElement = <HTMLElement>main.querySelector('#tab-content6');
        const vendorsLIList: HTMLElement = <HTMLElement>main.querySelector('#tab-content7');
        const addExtCusVendorsList: HTMLElement = <HTMLElement>main.querySelector('#tab-content8');

        manageLIs(purposesList);
        manageLIs(specialPurposesList);
        manageLIs(featuresList);
        manageLIs(specialFeaturesList);
        manageLIs(purposesLIList);
        manageLIs(vendorsList);
        manageLIs(vendorsLIList);
        manageLIs(addExtCusVendorsList);

        manageVendors(vendorsList);
        manageVendors(vendorsLIList);

        // Añadimos controladores de los botones
        this.addButtonsAcceptRejectControllers(main);

        // Añadimos controladores del header
        this.addHeaderControllers(main);

        // Añadimos controlador del footer
        this.addFooterController(main, callbacks);

        // Comprobar botón de guardar
        this.checkAllSetted(main);
    }

    /**
     * Carga las traducciones del CMP.
     * @private
     */
    private async loadI18N(): Promise<ILangCMP> {
        if (!this._LANG) {
            const idioma: IIdioma = {
                idioma: site.language,
                idioma_corto: site.language.split('-')[0],
                defecto: 'en',
                defecto_corto: 'en'
            };
            const lang = traduccion(idioma);
            const [
                back,
                almacena_info,
                props_caracts,
                tiempo_almacena,
                props_li,
                empresas,
                propositos,
                propositos_esp,
                caracteristicas,
                caracteristicas_esp,
                aceptar,
                rechazar,
                aceptar_todo,
                rechazar_todo,
                guardar_cerrar,
                aceptar_todo_y_guardar,
                consent_expl,
                consent_li,
                empresas_no_iab,
                politica_privacidad,
                cookies_almacena_cookies,
                cookies_almacena_otro,
                mas_info,
                dominio,
                categorias_datos,
                tiempo_retencion,
                tiempo_retencion_msg,
                interes_legitimo,
                obligatorio
            ] = await Promise.all([
                lang.volver,
                lang.cmp_almacena_info,
                lang.cmp_propos_caracts,
                lang.cmp_tiempo_almacena,
                lang.cmp_propos_li,
                lang.cmp_empresas,
                lang.cmp_propositos,
                lang.cmp_propos_espec,
                lang.cmp_caracteristicas,
                lang.cmp_caracts_espec,
                lang.aceptar,
                lang.rechazar,
                lang.aceptar_todo,
                lang.rechazar_todo,
                lang.guardar_cerrar,
                lang.aceptar_todo_guardar,
                lang.cmp_consent_expl_titulo,
                lang.cmp_consent_li_titulo,
                lang.cmp_emp_no_iab_titulo,
                lang.politica_de_privacidad,
                lang.cmp_cookies_almacena_1,
                lang.cmp_cookies_almacena_2,
                lang.mas_informacion,
                lang.dominio,
                lang.cmp_data_declarations_titulo,
                lang.cmp_data_retention_titulo,
                lang.cmp_data_retention_info(),
                lang.cmp_leg_int_claim,
                lang.obligatorio
            ]);

            this._LANG = {
                back,
                almacena_info,
                props_caracts,
                tiempo_almacena,
                props_li,
                empresas,
                propositos,
                propositos_esp,
                caracteristicas,
                caracteristicas_esp,
                aceptar,
                rechazar,
                aceptar_todo,
                rechazar_todo,
                guardar_cerrar,
                aceptar_todo_y_guardar,
                consent_expl,
                consent_li,
                empresas_no_iab,
                politica_privacidad,
                cookies_almacena_cookies,
                cookies_almacena_otro,
                mas_info,
                dominio,
                categorias_datos,
                tiempo_retencion,
                tiempo_retencion_msg,
                interes_legitimo,
                obligatorio
            };
        }
        return this._LANG;
    }

    /**
     * Añade el control sobre los botones de aceptar/rechazar.
     * @param layer Capa sobre la que actuar.
     */
    private addButtonsAcceptRejectControllers(layer: HTMLElement): void {
        // Añadimos el control sobre los botones de aceptar/rechazar
        (Array.from(layer.querySelectorAll('.disclaim, .accept')) as HTMLElement[]).forEach(button => {
            const isAccept: boolean = button.classList.contains('accept');
            const isP: boolean = button.dataset['p'] != undefined;
            const isPLI: boolean = button.dataset['pli'] != undefined;
            const isSF: boolean = button.dataset['sf'] != undefined;
            const isV: boolean = button.dataset['v'] != undefined;
            const isVLI: boolean = button.dataset['vli'] != undefined;
            const isAV: boolean = button.dataset['av'] != undefined;
            const isCV: boolean = button.dataset['cv'] != undefined;
            let id: number = isP
                ? parseInt(button.dataset['p']??'0')
                : (isPLI
                    ? parseInt(button.dataset['pli']??'0')
                    : (isSF
                        ? parseInt(button.dataset['sf']??'0')
                        : (isV
                            ? parseInt(button.dataset['v']??'0')
                            : (isVLI
                                ? parseInt(button.dataset['vli']??'0')
                                    : (isAV
                                        ? parseInt(button.dataset['av']??'0')
                                        : (isCV
                                            ? parseInt(button.dataset['cv']??'0')
                                            : -1))
                            )
                        )
                    )
                )
            ;

            button.onclick = () => {
                if (id >= 0) {
                    if (isP) cmp().userSelection?.setPurpose(id, isAccept);
                    else if (isPLI) cmp().userSelection?.setLIPurpose(id, isAccept);
                    else if (isSF) cmp().userSelection?.setSpecialFeature(id, isAccept);
                    else if (isV) cmp().userSelection?.setVendor(id, isAccept);
                    else if (isVLI) cmp().userSelection?.setVendorLI(id, isAccept);
                    else if (isAV) cmp().userSelection?.setAdditionalVendor(id, isAccept);
                    else if (isCV) cmp().userSelection?.setCustomVendor(id, isAccept);

                    if (isP) {
                        this.SELECTION.setPurpose(id, isAccept);
                    } else if (isPLI) {
                        this.SELECTION.setPurposeLI(id, isAccept);
                    } else if (isSF) {
                        this.SELECTION.setSpecialFeature(id, isAccept);
                    } else if (isV) {
                        this.SELECTION.setVendor(id, isAccept);
                    } else if (isVLI) {
                        this.SELECTION.setVendorLI(id, isAccept);
                    } else if (isAV) {
                        this.SELECTION.setAdditionalVendor(id, isAccept);
                    } else if (isCV) {
                        this.SELECTION.setCustomVendor(id, isAccept);
                    }

                    if (!this.SELECTION.areAllRejected() && !this.SELECTION.areAllSelected()) {
                        // Obtenemos el header
                        const header: HTMLElement = this.getHeaderElement(layer);
                        if (header) {
                            // Buscamos botones aceptar/rechazar
                            const buttons: HTMLElement[] = Array.from(header.querySelectorAll('.disclaim, .accept'));
                            // Eliminamos la clase selected de todos los botones
                            buttons.forEach(button => {
                                button.classList.remove('selected');
                            });
                        }
                    }

                } else {
                    cmp().userSelection?.setAllPurposesAndSpecialFeatures(isAccept);
                    cmp().userSelection?.setAllPurposesLI(isAccept);
                    cmp().userSelection?.setAllVendors(isAccept);

                    [
                        'tab-content1', 'tab-content4', 'tab-content5', 'tab-content6', 'tab-content7', 'tab-content8'
                    ].forEach(elementId => {
                        const element: HTMLElement = <HTMLElement>document.getElementById(elementId);
                        if (element) {
                            (Array.from(element.querySelectorAll('.disclaim, .accept')) as HTMLElement[]).forEach(button => {
                                if (isAccept) {
                                    if (button.classList.contains('disclaim')) button.classList.remove('selected');
                                    else button.classList.add('selected');
                                } else {
                                    if (button.classList.contains('disclaim')) button.classList.add('selected');
                                    else button.classList.remove('selected');
                                }
                            });
                        }
                    });

                    this.SELECTION.setAllPurposes(isAccept);
                    this.SELECTION.setAllPurposesLI(isAccept)
                    this.SELECTION.setAllVendors(isAccept);

                    // Lanazamos evento analítico
                    AnalyticManager().register({
                        action: EAction.CONFIGURAR,
                        label: isAccept ? ELabel.ACEPTAR_TODO : ELabel.RECHAZAR_TODO
                    });
                }

                //Deseleccionamos botón alternativo
                const selectedButton: HTMLElement = <HTMLElement>button.parentElement?.getElementsByClassName('selected')[0];
                if (selectedButton) {
                    selectedButton.classList.remove('selected');
                }

                // Seleccionamos botón
                button.classList.add('selected');

                // Comprobar botón de guardar
                this.checkAllSetted(layer);
            };
        });
    }

    /**
     * Añade controles sobre los elementos del header del Cmp.
     * @param layer Capa donde está el header.
     */
    private addHeaderControllers(layer: HTMLElement): void {
        const header: HTMLElement = this.getHeaderElement(layer);
        if (header) {
            // Cerrar
            const close: HTMLElement = <HTMLElement>header.querySelector('.nav_close');
            if (close) {
                close.onclick = () => {
                    publish<ICloseEvent>(EPropVendLayerEvent.CLOSE, {
                        allSelected: this.SELECTION.areAllSelected()
                    })
                };
            }
        }
    }

    /**
     * Añade controles sobre los elementos del footer del Cmp.
     * @param layer Capa donde está el footer.
     * @param callbacks Callbacks a ejecutar al pulsar el botón de guardar.
     */
    private addFooterController(layer: HTMLElement, callbacks: UICallback[] = []): void {
        // Obtenemos el footer
        const footer: HTMLElement = <HTMLElement>layer.getElementsByClassName('footer')[0];
        if (footer) {
            // Guardar y cerrar
            const saveAndClose: HTMLElement = <HTMLElement>footer.querySelector('.bt-basic.bt-izq-m');
            if (saveAndClose) {
                saveAndClose.onclick = async () => {
                    const enabled:boolean = this.SELECTION.allSetted();
                    if (enabled) {

                        publish<ISaveEvent>(EPropVendLayerEvent.GUARDAR, {
                            allSelected: this.SELECTION.areAllSelected(),
                            allRejected: this.SELECTION.areAllRejected(),
                            callbacks
                        });
                    } else {
                        // Lanzamos evento analítico
                        AnalyticManager().register({
                            action: EAction.CONFIGURAR,
                            label: ELabel.GUARDAR_CERRAR_DESHABILITADO
                        });
                    }
                };
            }

            // Aceptar todos los consentimientos y guardar
            const acceptAllAndSave:HTMLElement = <HTMLElement>footer.getElementsByClassName('accept-save')[0];
            if (acceptAllAndSave) {
                acceptAllAndSave.onclick = async () => {

                    publish<IAcceptAllEvent>(EPropVendLayerEvent.ACEPTAR_TODO, {
                        callbacks
                    });
                };
            }
        }
    }

    /**
     * Comprueba si todos los elementos de la lista están seleccionados y si es así, activa el botón de guardar y cerrar.
     * @param layer Capa de la lista.
     * @private
     */
    private checkAllSetted(layer: HTMLElement) : void {
        // Obtenemos el footer
        const footer: HTMLElement = <HTMLElement>layer.querySelector('.footer');
        if (footer) {
            // Guardar y cerrar
            const saveAndClose: HTMLElement = <HTMLElement>footer.querySelector('.bt-basic.bt-izq-m');
            if (saveAndClose) {
                if (this.SELECTION.allSetted()) {
                    saveAndClose.classList.remove('disabled');
                } else {
                    saveAndClose.classList.add('disabled');
                }
            }
        }
    }

    /**
     * Obtiene el elemento del header de la capa.
     * @param layer Capa donde buscar.
     * @private
     */
    private getHeaderElement(layer: HTMLElement): HTMLElement {
        return layer.querySelector('.header') as HTMLElement;
    }

    /**
     * Cierra la ventana del Cmp.
     * @private
     */
    public override close(): void {
        this.panel.parentElement?.parentElement?.parentElement?.removeChild(this.panel.parentElement.parentElement);

        // Lanzamos el evento de rechazo
        const event = UIEvent.create(TEvent.CIERRA_CMP, TEventSubtype.SECOND_LAYER);
        event.register();
        event.notify();
    }

}
